import { ReactNode } from "react";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  OfflineContextProvider,
  OfflineState,
  useOfflineContext,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/offline-context";
import { StaleContextProvider } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/stale-context";
import { ErrDocumentNotReadyContextProvider } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Check, RefreshCw } from "lucide-react";
import t from "@/lang/lang";

export function WorkOrderProviders({
  workOrderId,
  children,
}: {
  workOrderId: string;
  children: ReactNode;
}) {
  return (
    <OfflineContextProvider workOrderId={workOrderId}>
      <StaleContextProvider
        initiallyStale={[
          "ahuComponentCatalog",
          "airHandlingUnit",
          "airHandlingUnitData",
          "airMicrobialSampleSet",
          "devices",
          "dustDensitySample",
          "laboratories",
          "orders",
          "offers",
          "position",
          "positionCatalog",
          "structuralAssessmentCatalog",
          "surfaceMicrobialSampleSet",
          "visualAssessmentCatalog",
          "waterSample",
          "workOrder",
        ]}
      >
        <PersistenceContextProvider>
          <OfflineStateWrapper>
            <ErrDocumentNotReadyContextProvider>
              {children}
            </ErrDocumentNotReadyContextProvider>
          </OfflineStateWrapper>
        </PersistenceContextProvider>
      </StaleContextProvider>
    </OfflineContextProvider>
  );
}

function OfflineStateWrapper({ children }: { children: ReactNode }) {
  const { offlineState } = useOfflineContext();

  switch (offlineState) {
    case OfflineState.ONLINE:
    case OfflineState.OFFLINE:
      return children;
    case OfflineState.PREPARING_OFFLINE:
      return <PreparingOfflineCard />;
    case OfflineState.SYNCING:
      return <SynchronizingCard />;
    default:
      return null;
  }
}

function PreparingOfflineCard() {
  const { synced } = useOfflineContext();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Preparing Offline State")}</CardTitle>
      </CardHeader>
      <CardContent>
        <table>
          <tbody>
            {synced.map((item, index) => (
              <tr key={item}>
                <td className="p-2">
                  {index === synced.length - 1 ? (
                    <RefreshCw className="animate-spin" />
                  ) : (
                    <Check className="text-green-700" />
                  )}
                </td>
                <td className="p-2">{item}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function SynchronizingCard() {
  const { synced } = useOfflineContext();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Synchronisiere den Offline Status...")}</CardTitle>
      </CardHeader>
      <CardContent>
        <table>
          <tbody>
            {synced.map((item, index) => (
              <tr key={item}>
                <td className="p-2">
                  {index === synced.length - 1 ? (
                    <RefreshCw className="animate-spin" />
                  ) : (
                    <Check className="text-green-700" />
                  )}
                </td>
                <td className="p-2">{item}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
